/**
 * Clabs API Client for Omni Context
 * Handles all communication with the Clabs (Consilient Labs) backend
 * Based on the Garage Angular API service implementation
 */
import { config } from '../config/envConfig';

class ClabsAPI {
    constructor() {
        // Default URLs - can be configured via environment variables
        this.api_url = config.CLABS_API_URL;
        
        // Queue management properties
        this.queueOptimizedTimeMap = new Map();
        this.queueNumberOfRequestsMap = new Map();
        this.queueSet = new Set();
        this.queueRequests = ['clustering', 'retrospections', 'episodes', 'elements', 'services/backups', 'expertfinder', 'garage', 'classes'];
        this.optimizedTime = 2000;
        this.MAX_WAIT_TIME = 1000 * 60; // 60 seconds max wait time
    }

    /**
     * Make HTTP request to Clabs API with authentication
     * Includes queue handling for long-running operations
     */
    async request(endpoint, options = {}, shouldUseQueue = true) {
        const url = `${endpoint}`;
        
        // Check if this request should use queue
        if (this.isQueueItem(url) && shouldUseQueue) {
            return await this.handleQueueRequest(url, options);
        }
        
        // Get authorization token from localStorage
        const authToken = localStorage.getItem('cognito_id_token');

        console.log('🔧 Clabs API Request:', {
            endpoint,
            hasToken: !!authToken,
            tokenLength: authToken ? authToken.length : 0
        });

        const config = {
            headers: {
                'Content-Type': 'application/json',
                // Add authorization header if token exists
                ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                ...options.headers
            },
            ...options
        };

        try {
            const response = await fetch(url, config);
            
            if (!response.ok) {
                const errorText = await response.text();
                let errorMessage = `HTTP error! status: ${response.status}`;
                
                try {
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.message || errorData.error || errorMessage;
                } catch {
                    errorMessage = errorText || errorMessage;
                }
                
                throw new Error(errorMessage);
            }

            // Handle different response types
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return await response.json();
            }
            
            return await response.text();
        } catch (error) {
            console.error('Clabs API Error:', {
                endpoint,
                error: error.message,
                hasToken: !!authToken
            });
            throw error;
        }
    }

    /**
     * Check if URL should use queue system
     * @param {string} url - The URL to check
     * @returns {boolean} - Whether this URL should use queue
     */
    isQueueItem(url) {
        let isQueueToken = false;
        let reqUrl = url.replace(this.api_url, '');
        
        this.queueRequests.forEach((req) => {
            if (reqUrl.includes(req)) {
                isQueueToken = true;
            }
        });
        
        return isQueueToken;
    }

    /**
     * Check if request is currently in queue
     * @param {string} tokenId - The token ID
     * @returns {boolean} - Whether token is in queue
     */
    isInQueue(tokenId) {
        return this.queueSet.has(this.parseToken(tokenId));
    }

    /**
     * Parse token from queue response
     * @param {string} token - The token string
     * @returns {string} - Parsed token
     */
    parseToken(token) {
        return token.replace('/queue?token=', '');
    }

    /**
     * Handle queue-based request
     * @param {string} url - The request URL
     * @param {object} options - Request options
     * @returns {Promise} - Promise resolving to final result
     */
    async handleQueueRequest(url, options = {}) {
        const started = Date.now();
        
        // Get authorization token from localStorage
        const authToken = localStorage.getItem('cognito_id_token');

        const config = {
            headers: {
                'Content-Type': 'application/json',
                ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                ...options.headers
            },
            ...options
        };

        try {
            console.log('🔧 Starting queued Clabs request:', url);
            
            // Make initial request
            const response = await fetch(url, config);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const initialResult = await response.json();
            
            // Check if response contains a token (indicating async processing)
            if (initialResult.token) {
                console.log('🔧 Got queue token, starting polling:', initialResult.token);
                return await this.checkQueueStatus(initialResult, started, url);
            }
            
            // If no token, return result directly
            return initialResult;
            
        } catch (error) {
            console.error('Clabs Queue Request Error:', {
                url,
                error: error.message,
                hasToken: !!authToken
            });
            throw error;
        }
    }

    /**
     * Poll queue status until completion
     * @param {object} response - Initial response with token
     * @param {number} started - Start timestamp
     * @param {string} originalUrl - Original request URL
     * @returns {Promise} - Promise resolving to final result
     */
    async checkQueueStatus(response, started, originalUrl) {
        const timeNow = Date.now();
        let roundTripTime = timeNow;
        const roundTripTimeInitialRequest = timeNow - started;
        let isFirst = true;
        let numChecks = 0;
        const maxChecks = 120; // Maximum 2 minutes of polling (1s intervals)

        if (response?.token) {
            this.queueSet.add(this.parseToken(response.token));
        }

        // Determine the correct API base URL for polling
        let targetApi = this.api_url;

        while (numChecks < maxChecks) {
            try {
                await this.sleep(this.getDelay(numChecks, this.removeApiUrlParams(originalUrl)));
                
                if (!isFirst) {
                    roundTripTime = Date.now();
                }
                isFirst = false;
                numChecks++;

                console.log(`🔧 Polling queue status (attempt ${numChecks}):`, response.token);

                // Poll the queue endpoint
                const statusResponse = await this.request(targetApi + response.token, {}, false); // Don't use queue for polling
                
                console.log('🔧 Queue status response:', statusResponse);
                
                // Check if processing is complete (status 300 indicates completion)
                if (statusResponse.status === 300) {
                    if (statusResponse.response?.status === "error") {
                        throw new Error(statusResponse.response.error || 'Queue processing failed');
                    }
                    
                    // Calculate timing metrics
                    roundTripTime = Date.now() - roundTripTime;
                    const totalTime = Date.now() - started;
                    const queueTime = totalTime - roundTripTimeInitialRequest;
                    
                    console.log(`🔧 Queue completed:`, {
                        token: this.parseToken(response.token),
                        totalTime: `${totalTime}ms`,
                        queueTime: `${queueTime}ms`,
                        checks: numChecks
                    });
                    
                    // Clean up
                    if (response?.token) {
                        this.queueSet.delete(this.parseToken(response.token));
                    }
                    
                    // Update queue timing statistics
                    this.calculateNewQueueAVG(this.removeApiUrlParams(originalUrl), queueTime);
                    
                    return statusResponse.response;
                }
                
                // Continue polling if not complete
                
            } catch (error) {
                console.error('Queue polling error:', error);
                
                // Clean up on error
                if (response?.token) {
                    this.queueSet.delete(this.parseToken(response.token));
                }
                
                throw error;
            }
        }
        
        // Timeout handling
        if (response?.token) {
            this.queueSet.delete(this.parseToken(response.token));
        }
        
        throw new Error(`Queue processing timeout after ${numChecks} attempts`);
    }

    /**
     * Sleep utility function
     * @param {number} ms - Milliseconds to sleep
     * @returns {Promise} - Promise that resolves after delay
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Calculate polling delay based on request history
     * @param {number} numChecks - Number of checks already performed
     * @param {string} requestType - Type of request for optimization
     * @returns {number} - Delay in milliseconds
     */
    getDelay(numChecks = 0, requestType = '') {
        let delay = 2000; // Default 2 second delay
        
        if (requestType) {
            const avgOptimizedTime = this.queueOptimizedTimeMap.get(requestType);
            if (avgOptimizedTime && avgOptimizedTime > 0 && avgOptimizedTime < (1000 * 10)) {
                delay = avgOptimizedTime;
            }
        }

        if (numChecks > 1) {
            // Increase delay based on number of checks already performed
            delay = delay * (1 + numChecks / 5);
        }

        // 10% of requests get instant check for optimization
        const finalDelay = Math.random() < 0.1 ? 1 : delay;
        
        return Math.min(finalDelay, this.MAX_WAIT_TIME / 60); // Cap at 1 second max
    }

    /**
     * Update queue timing averages
     * @param {string} request - Request type
     * @param {number} time - Time taken
     */
    calculateNewQueueAVG(request, time) {
        let avgTime = this.queueOptimizedTimeMap.get(request);
        
        if (avgTime && avgTime < this.MAX_WAIT_TIME) {
            const numTimes = this.queueNumberOfRequestsMap.get(request);
            
            if (numTimes && numTimes < 30) {
                avgTime = ((avgTime * numTimes) + time) / (numTimes + 1);
                this.queueNumberOfRequestsMap.set(request, numTimes + 1);
                this.queueOptimizedTimeMap.set(request, avgTime);
            } else if (numTimes && numTimes >= 30) {
                // Reset after 30 requests
                this.queueNumberOfRequestsMap.set(request, 1);
                this.queueOptimizedTimeMap.set(request, time);
            }
        } else {
            this.queueOptimizedTimeMap.set(request, this.MAX_WAIT_TIME);
            this.queueNumberOfRequestsMap.set(request, 1);
        }
    }

    /**
     * Remove URL parameters for categorization
     * @param {string} url - URL to clean
     * @returns {string} - URL without parameters
     */
    removeApiUrlParams(url) {
        return url.split('?')[0];
    }

    /**
     * Get garage status - returns cluster and memory information
     * Uses the main API endpoint (matches restapi.ts implementation)
     * @returns Promise containing garage status with clusters and memories
     */
    async garageStatus() {
        return await this.request(`${this.api_url}/garage`);
    }

    /**
     * Perform garage query/search
     * @param {string} text - The search text
     * @param {string} threshold - Similarity threshold
     * @param {string} distance - Distance algorithm to use
     * @param {boolean} objectLatestState - Whether to use latest object state
     * @param {boolean} useLemma - Whether to use lemmatization
     * @returns Promise containing garage search results
     */
    async garage(text, threshold = "1", distance = 'ochiai', objectLatestState = true, useLemma = false) {
        const payload = {
            'distance': distance,
            'threshold': threshold,
            'object_latest_state': objectLatestState,
            'use_lemma': useLemma,
            'plain_text': text
        };

        return await this.request(`${this.api_url}/garage`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    }

    /**
     * Get all memories from Clabs
     * @returns Promise containing list of memories
     */
    async getMemories() {
        return await this.request(`${this.api_url}/memories`);
    }

    /**
     * Get information about a specific memory
     * @param {string} memoryName - Name of the memory
     * @returns Promise containing memory information
     */
    async getMemoryInfo(memoryName) {
        return await this.request(`${this.api_url}/memories/${memoryName}`);
    }


    /**
     * Get elements by object ID
     * @param {string} memoryName - Name of the memory
     * @param {string[]} objectIds - Array of object IDs
     * @param {string} from - Lower bound (optional)
     * @param {string} to - Upper bound (optional)
     * @returns Promise containing elements
     */
    async getElementsByObjectId(memoryName, objectIds, from = "0", to = "0") {
        const payload = {
            'objects_id': objectIds,
            'lower_bound': from,
            'upper_bound': to
        };

        return await this.request(`${this.api_url}/memories/${memoryName}/elements`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    }

    /**
     * Get available services
     * @param {boolean} refresh - Whether to refresh the service list
     * @returns Promise containing services information
     */
    async getServices() {
        return await this.request(`${this.api_url}/services`);
    }


    /**
     * Test Clabs connection by performing a health check
     * @returns Promise indicating connection status
     */
    async testConnection() {
        try {
            // Try to get garage status as a connection test
            const status = await this.garageStatus();
            return {
                success: true,
                message: 'Connection successful',
                status: status
            };
        } catch (error) {
            return {
                success: false,
                message: `Connection failed: ${error.message}`,
                error: error
            };
        }
    }

    /**
     * Get cluster names from garage status
     * This is used to populate available sources
     * @returns Promise containing array of cluster names
     */
    async getAvailableSources() {
        try {
            const status = await this.garageStatus();
            const clusterNames = [];
            
            if (status.clusters) {
                Object.keys(status.clusters).forEach(clusterName => {
                    if (clusterName && !clusterNames.includes(clusterName)) {
                        clusterNames.push(clusterName);
                    }
                });
            }

            console.log('🔧 Available sources (clusters):', clusterNames);
            
            return clusterNames;
        } catch (error) {
            console.error('Error getting available sources:', error);
            throw error;
        }
    }

    /**
     * Get configuration for a Clabs connection
     * @returns Object containing default configuration
     */
    getDefaultConfig() {
        return {
            api_url: this.api_url,
            authentication: {
                type: 'cognito',
                requiresToken: true
            },
            features: {
                memorySearch: true,
                classificationSupport: true,
                textSimilarity: true
            }
        };
    }

    /**
     * Update API URL (for configuration purposes)
     * @param {string} apiUrl - Main API URL
     */
    updateUrls(apiUrl) {
        if (apiUrl) this.api_url = apiUrl;
    }
}

// Create global Clabs API client instance
const clabsApiClient = new ClabsAPI();

export { ClabsAPI, clabsApiClient };
